import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Printer, FileText, History } from 'lucide-react';

import SchedulePaymentPanel from '../../../components/payroll/SchedulePaymentPanel';
import HeroCard from '../components/HeroCard';
import MetricTile from '../components/MetricTile';
import ToolbarBtn from '../components/ToolbarBtn';
import SectionHead from '../components/SectionHead';
import ObligationCard from '../components/ObligationCard';
import BreakdownPanel from '../components/BreakdownPanel';
import EmptyBox from '../components/EmptyBox';
import ErrorStrip from '../components/ErrorStrip';
import ResourceLinkList from '../components/ResourceLinkList';

import { money } from '../helpers';
import { toolbarStyle } from '../styles';
import { UPCOMING_FILINGS_COUNT } from '../constants';
import type { PD7A, Obligation, PaymentsFilters } from '../types';

const SchedulePaymentPanelAny = SchedulePaymentPanel as any;

interface Props {
  pd7a: PD7A | null;
  loading: boolean;
  error: string;
  onResourcesOpen: () => void;
  onPrint: () => void;
}

export default function PaymentsTab({ pd7a, loading, error, onResourcesOpen, onPrint }: Props) {
  const navigate = useNavigate();
  const [payObligation, setPayObligation] = useState<Obligation | null>(null);
  const [breakdownOpen, setBreakdownOpen] = useState<boolean>(false);
  // Filter popovers are not currently rendered; state kept for future.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filters, setFilters] = useState<PaymentsFilters>({
    jurisdictions: [],
    periods: [],
    statuses: [],
  });

  const hasData = Boolean(pd7a && pd7a.paycheque_count > 0);
  const currentPayment = pd7a ? pd7a.current_payment : 0;
  const dueDateDisplay = pd7a ? pd7a.due_date_display : '';
  const status = pd7a ? pd7a.status : 'no_activity';
  const periodLabel = pd7a ? pd7a.period_label : '';

  const buildObligation = (): Obligation => ({
    taxName: 'Federal Taxes',
    period: periodLabel,
    liability: periodLabel,
    dueDate: dueDateDisplay,
    amount: currentPayment.toFixed(2),
    status: status === 'overdue' ? 'pastDue' : 'due',
    breakdown: pd7a ? [
      ['Income tax deducted (federal)', pd7a.tax_deductions.toFixed(2)],
      ['CPP contributions (employee and employer)', (pd7a.cpp_employee + pd7a.cpp_employer).toFixed(2)],
      ['EI premiums (employee and employer)', (pd7a.ei_employee + pd7a.ei_employer).toFixed(2)],
    ] : [],
  });

  return (
    <>
      {hasData && pd7a && (
        <HeroCard
          dueDate={dueDateDisplay}
          amount={currentPayment}
          status={status}
          periodLabel={periodLabel}
          onPayFile={() => setPayObligation(buildObligation())}
        />
      )}

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 12, marginTop: hasData ? 0 : 18,
      }}>
        <MetricTile label="Due now" value={loading ? 'Loading...' : money(hasData ? currentPayment : 0)} />
        <MetricTile label="Paid this year" value={loading ? 'Loading...' : (pd7a && pd7a.ytd_total ? money(pd7a.ytd_total) : '$0.00')} />
        <MetricTile label="Upcoming filings" value={String(UPCOMING_FILINGS_COUNT)} />
      </div>

      <div style={toolbarStyle}>
        <div style={{ flex: 1 }} />
        <ToolbarBtn icon={<Printer size={14} strokeWidth={2.5} />} label="Print" onClick={onPrint} />
        <ToolbarBtn icon={<FileText size={14} strokeWidth={2.5} />} label="Resources" onClick={onResourcesOpen} />
        <ToolbarBtn icon={<History size={14} strokeWidth={2.5} />} label="Payment history" onClick={() => navigate('/payroll/taxes/archived')} />
      </div>

      {error && <ErrorStrip text={error} />}

      <SectionHead label="ACTION NEEDED" count={hasData ? 1 : 0} />
      {hasData && pd7a ? (
        <>
          <ObligationCard
            title="Federal taxes"
            sub={periodLabel}
            status={status}
            dueDate={dueDateDisplay}
            amount={money(currentPayment)}
            breakdownOpen={breakdownOpen}
            onToggleBreakdown={() => setBreakdownOpen(!breakdownOpen)}
            onPayFile={() => setPayObligation(buildObligation())}
          />
          {breakdownOpen && (
            <BreakdownPanel
              rows={[
                { label: 'Canada Pension Plan Employer', amount: pd7a.cpp_employer },
                { label: 'Canada Pension Plan', amount: pd7a.cpp_employee },
                { label: 'Employment Insurance Employer', amount: pd7a.ei_employer },
                { label: 'Employment Insurance', amount: pd7a.ei_employee },
                { label: 'Income Tax', amount: pd7a.tax_deductions },
              ]}
              total={pd7a.current_payment}
            />
          )}
        </>
      ) : (
        <EmptyBox text="No remittance due this month." />
      )}

      <SectionHead label="COMING UP" count={0} />
      <EmptyBox text="Upcoming payments will show here as they're calculated." />

      <SectionHead label="SCHEDULED" count={0} />
      <EmptyBox text="Payments you schedule ahead of time will show here." />

      <SchedulePaymentPanelAny
        open={Boolean(payObligation)}
        onClose={() => setPayObligation(null)}
        obligation={payObligation}
        onPaid={() => { setPayObligation(null); }}
      />

      <SectionHead label="PAYMENT RESOURCES" />
      <ResourceLinkList
        items={[
          { title: 'Record tax payments', desc: 'Add or update tax payments made outside of Novala.' },
          { title: 'All payment resources', desc: 'Payment history, tax liabilities, and other reports.' },
          { title: 'Remittance forms (twice a month)', desc: 'Threshold 1 payroll remittances, source deductions, and company contributions.' },
          { title: 'Remittance forms (four times a month)', desc: 'Threshold 2 payroll remittances, source deductions, and company contributions.' },
          { title: 'Remittance forms (monthly)', desc: 'Monthly payroll remittance, source deductions, and company contributions.' },
          { title: 'Remittance forms (quarterly)', desc: 'Quarterly payroll remittance, source deductions, and company contributions.' },
        ]}
      />
    </>
  );
}